import { faCheck, faPen } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components";

import { ScenarioMessageTypes } from "../../enums/ScenarioMessageTypes.enum";
import { IConnectedUser } from "../../interfaces/IConnectedUser.interface";
import {
	clockOf,
	EntryRow,
	KEY_MESSAGES,
	nameOf,
	PROTOCOL_TEXT_LIMIT,
	recordedAtOf,
	shortNameOf,
	shownAt,
} from "../../utils/protocolForm";
import { useProtocolText } from "./useProtocolText";

interface ProtocolEntryRowProps {
	row: EntryRow;
	canKeep: boolean;
	assignable: IConnectedUser[];
	userById: (id?: string | null) => IConnectedUser | undefined;
	onAssign: (messageId: string, assigneeId: string | null) => void;
	onToggleDone: (messageId: string, done: boolean) => void;
	onEdit: (messageId: string, message: string, recordedAt: number) => void;
}

/** One line of the form: Aufzeichnung · Uhrzeit · zuständig · erledigt (BCM360-376). */
export default function ProtocolEntryRow({
	row,
	canKeep,
	assignable,
	userById,
	onAssign,
	onToggleDone,
	onEdit,
}: ProtocolEntryRowProps) {
	const { t, i18n } = useTranslation();
	const tf = useProtocolText();
	const { message } = row;
	const data = message.data ?? {};
	const isDecision = message.type === ScenarioMessageTypes.DECISION;
	const isKeyMessage = !isDecision && KEY_MESSAGES.includes(data.message);
	const assignee = userById(data.assigneeId);
	const isDone = !!data.assigneeId && !!data.doneAt;

	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");
	const [draftTime, setDraftTime] = useState("");
	const [timeInvalid, setTimeInvalid] = useState(false);

	const text = isDecision
		? data.name || t("courses.progress.next")
		: i18n.exists(`courses.progress.${data.message}`)
			? t(`courses.progress.${data.message}`, {
					...(data.options ?? {}),
					interpolation: { escapeValue: false },
				})
			: data.message;

	const optionLabel = (option?: string) =>
		option === "next-phase" ? t("courses.progress.next") : (option ?? "");

	const author = nameOf({ firstName: data.firstName, lastName: data.lastName });
	const editor = data.editedAt ? nameOf(userById(data.editedBy)) : "";

	const startEdit = () => {
		setDraft(data.message ?? "");
		setDraftTime(clockOf(shownAt(message)));
		setTimeInvalid(false);
		setEditing(true);
	};

	const saveEdit = () => {
		const recordedAt = recordedAtOf(draftTime, shownAt(message));

		if (recordedAt === null) {
			setTimeInvalid(true);

			return;
		}

		if (!draft.trim()) {
			return;
		}

		onEdit(message.id, draft, recordedAt);
		setEditing(false);
	};

	const onEditKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			saveEdit();
		} else if (event.key === "Escape") {
			setEditing(false);
		}
	};

	return (
		<tr className="group bg-white" data-entry-id={message.id}>
			<td className="relative border border-border px-3 py-2 align-top text-sm text-text group-hover:bg-neutral-25">
				{editing ? (
					<div className="flex flex-col gap-1">
						<textarea
							aria-label={tf("textLabel")}
							className="min-h-[58px] w-full resize-y rounded-md border border-border px-2 py-1.5 outline-none focus:border-accent"
							maxLength={PROTOCOL_TEXT_LIMIT}
							value={draft}
							onChange={(event) => setDraft(event.target.value)}
							onKeyDown={onEditKey}
						/>
						<div className="flex items-center justify-between gap-2 text-xs text-subtle">
							<span>
								{draft.length} / {PROTOCOL_TEXT_LIMIT}
							</span>
							<span className="flex gap-2">
								<button
									className="rounded-md border border-border px-2 py-1 font-semibold text-text hover:border-accent"
									type="button"
									onClick={() => setEditing(false)}
								>
									{tf("cancel")}
								</button>
								<button
									className="rounded-md bg-ink px-2 py-1 font-semibold text-white hover:bg-neutral-700"
									type="button"
									onClick={saveEdit}
								>
									{tf("save")}
								</button>
							</span>
						</div>
					</div>
				) : (
					<>
						<span
							className={classNames("block whitespace-pre-wrap break-words pr-6", {
								"text-subtle": isDone,
								italic: isKeyMessage,
							})}
						>
							{isDecision && (
								<span className="mr-1.5 inline-block rounded bg-ink px-1.5 align-[1px] text-[11px] font-bold uppercase tracking-wide text-white">
									{tf("decision")}
								</span>
							)}
							{data.message === "requested-spoiler" && (
								<span className="mr-1.5 inline-block rounded bg-neutral-100 px-1.5 align-[1px] text-[11px] font-bold uppercase tracking-wide text-neutral-700">
									{tf("help")}
								</span>
							)}
							{text}
						</span>
						<span className="mt-0.5 block text-xs text-subtle">
							{isDecision
								? [
										tf("chosen", { option: optionLabel(data.finalDecision?.option) }),
										data.explanationText ? tf("explanation", { text: data.explanationText }) : "",
									]
										.filter(Boolean)
										.join(" · ")
								: author
									? tf("writtenBy", { name: author })
									: ""}
							{typeof data.recordedInPhaseId === "number" && (
								<span className="ml-1.5 inline-block rounded bg-neutral-100 px-1.5 text-[11px] font-semibold leading-[17px] text-neutral-700">
									{tf("phase", { id: data.recordedInPhaseId })}
								</span>
							)}
						</span>
						{editor && (
							<span className="block text-xs text-subtle">
								{tf("editedBy", { name: editor, time: clockOf(data.editedAt) })}
							</span>
						)}
						{canKeep && !isDecision && !isKeyMessage && (
							<button
								aria-label={tf("edit")}
								className="absolute right-2 top-2 hidden h-6 w-6 items-center justify-center rounded text-subtle hover:text-ink group-hover:flex focus:flex"
								title={tf("edit")}
								type="button"
								onClick={startEdit}
							>
								<Icon className="h-3.5 w-3.5" icon={faPen} />
							</button>
						)}
					</>
				)}
			</td>
			<td className="border border-border px-3 py-2 align-top text-sm tabular-nums text-text group-hover:bg-neutral-25">
				{editing ? (
					<input
						aria-invalid={timeInvalid}
						aria-label={tf("timeLabel")}
						className={classNames(
							"w-full rounded-md border px-1.5 py-1 tabular-nums outline-none focus:border-accent",
							timeInvalid ? "border-danger" : "border-border"
						)}
						inputMode="numeric"
						maxLength={5}
						value={draftTime}
						onChange={(event) => {
							setDraftTime(event.target.value);
							setTimeInvalid(false);
						}}
					/>
				) : (
					clockOf(shownAt(message))
				)}
			</td>
			<td className="border border-border px-2 py-1.5 align-top text-sm group-hover:bg-neutral-25">
				{canKeep ? (
					<select
						aria-label={tf("assigneeLabel")}
						className={classNames(
							"w-full cursor-pointer rounded-md border border-transparent bg-transparent px-1.5 py-1 text-[13px] hover:border-border focus:border-accent",
							assignee ? "text-text" : "text-transparent group-hover:text-muted focus:text-text"
						)}
						value={data.assigneeId ?? ""}
						onChange={(event) => onAssign(message.id, event.target.value || null)}
					>
						<option className="text-text" value="">
							{tf("assign")}
						</option>
						{assignable.map((user) => (
							<option key={user.id} className="text-text" value={user.id}>
								{nameOf(user)}
							</option>
						))}
						{assignee && !assignable.some((user) => user.id === assignee.id) && (
							<option className="text-text" value={assignee.id}>
								{nameOf(assignee)}
							</option>
						)}
					</select>
				) : (
					<span className="block px-1.5 py-1 text-[13px] text-text">{nameOf(assignee)}</span>
				)}
			</td>
			<td className="border border-border px-3 py-2 align-top text-sm group-hover:bg-neutral-25">
				{data.assigneeId && (
					<button
						aria-checked={isDone}
						aria-label={tf("doneLabel")}
						className={classNames("flex items-start gap-2 text-left", {
							"cursor-default": !canKeep,
						})}
						disabled={!canKeep}
						role="checkbox"
						type="button"
						onClick={() => onToggleDone(message.id, !isDone)}
					>
						<span
							className={classNames(
								"mt-px flex h-[18px] w-[18px] flex-none items-center justify-center rounded border-[1.5px]",
								isDone ? "border-success bg-success" : "border-grey-soft bg-white"
							)}
						>
							{isDone && <Icon className="h-3 w-3 text-white" icon={faCheck} />}
						</span>
						{isDone ? (
							<span className="text-xs leading-snug text-subtle">
								<b className="block text-[13px] font-semibold text-text">
									{tf("doneAt", { time: clockOf(data.doneAt) })}
								</b>
								{shortNameOf(userById(data.doneBy))}
							</span>
						) : (
							<span className="text-[13px] font-semibold text-accent-strong">{tf("open")}</span>
						)}
					</button>
				)}
			</td>
		</tr>
	);
}
